import type { GoldZoneSettings, Zone } from "../types/domain";

export interface CalculationResult {
  value: number | null;
  status: "ok" | "unavailable" | "invalid";
  message?: string;
}

export const unavailable = (message = "Inputs unavailable"): CalculationResult => ({
  value: null,
  status: "unavailable",
  message
});

export const invalid = (message: string): CalculationResult => ({
  value: null,
  status: "invalid",
  message
});

export const ok = (value: number): CalculationResult => ({ value, status: "ok" });

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const safeDivide = (numerator: number | null | undefined, denominator: number | null | undefined): CalculationResult => {
  if (!isFiniteNumber(numerator) || !isFiniteNumber(denominator)) return unavailable();
  if (denominator === 0) return invalid("Denominator is zero");
  return ok(numerator / denominator);
};

export const revenueGrowth = (currentRevenue: number | null, priorRevenue: number | null): CalculationResult => {
  if (!isFiniteNumber(currentRevenue) || !isFiniteNumber(priorRevenue)) return unavailable();
  if (priorRevenue <= 0) return invalid("Prior revenue must be positive");
  return ok((currentRevenue - priorRevenue) / priorRevenue);
};

export const fiveYearCagr = (beginningValue: number | null, endingValue: number | null, years = 5): CalculationResult => {
  if (!isFiniteNumber(beginningValue) || !isFiniteNumber(endingValue)) return unavailable();
  if (years <= 0) return invalid("Years must be positive");
  if (beginningValue <= 0 || endingValue < 0) return invalid("CAGR requires positive beginning value and non-negative ending value");
  return ok(Math.pow(endingValue / beginningValue, 1 / years) - 1);
};

export const returnVersusBenchmark = (expectedAnnualReturn: number | null, benchmark = 0.11): CalculationResult => {
  if (!isFiniteNumber(expectedAnnualReturn)) return unavailable();
  return ok(expectedAnnualReturn - benchmark);
};

export const roe = (netIncome: number | null, shareholdersEquity: number | null): CalculationResult => {
  if (isFiniteNumber(shareholdersEquity) && shareholdersEquity < 0) return invalid("ROE is not meaningful with negative equity");
  return safeDivide(netIncome, shareholdersEquity);
};

export const roic = (
  operatingIncome: number | null,
  taxRate: number | null,
  totalDebt: number | null,
  shareholdersEquity: number | null,
  cashAndEquivalents: number | null
): CalculationResult => {
  if (!isFiniteNumber(operatingIncome) || !isFiniteNumber(taxRate)) return unavailable();
  if (!isFiniteNumber(totalDebt) || !isFiniteNumber(shareholdersEquity) || !isFiniteNumber(cashAndEquivalents)) return unavailable();
  const investedCapital = totalDebt + shareholdersEquity - cashAndEquivalents;
  if (shareholdersEquity < 0 || investedCapital <= 0) return invalid("ROIC requires positive invested capital");
  return ok((operatingIncome * (1 - taxRate)) / investedCapital);
};

export const operatingMargin = (operatingIncome: number | null, revenue: number | null): CalculationResult =>
  safeDivide(operatingIncome, revenue);

export const netMargin = (netIncome: number | null, revenue: number | null): CalculationResult => safeDivide(netIncome, revenue);

export const debtToEquity = (totalDebt: number | null, shareholdersEquity: number | null): CalculationResult => {
  if (isFiniteNumber(shareholdersEquity) && shareholdersEquity < 0) return invalid("Debt-to-equity is not meaningful with negative equity");
  return safeDivide(totalDebt, shareholdersEquity);
};

export const currentRatio = (currentAssets: number | null, currentLiabilities: number | null): CalculationResult =>
  safeDivide(currentAssets, currentLiabilities);

export const quickRatio = (
  currentAssets: number | null,
  inventory: number | null,
  currentLiabilities: number | null
): CalculationResult => {
  if (!isFiniteNumber(currentAssets) || !isFiniteNumber(inventory)) return unavailable();
  return safeDivide(currentAssets - inventory, currentLiabilities);
};

export const interestCoverage = (operatingIncome: number | null, interestExpense: number | null): CalculationResult => {
  if (!isFiniteNumber(interestExpense)) return unavailable();
  return safeDivide(operatingIncome, Math.abs(interestExpense));
};

export const freeCashFlow = (operatingCashFlow: number | null, capitalExpenditure: number | null): CalculationResult => {
  if (!isFiniteNumber(operatingCashFlow) || !isFiniteNumber(capitalExpenditure)) return unavailable();
  return ok(operatingCashFlow + capitalExpenditure);
};

export const freeCashFlowMargin = (fcf: number | null, revenue: number | null): CalculationResult => safeDivide(fcf, revenue);

export const dividendYield = (annualDividendPerShare: number | null, currentPrice: number | null): CalculationResult =>
  safeDivide(annualDividendPerShare, currentPrice);

export const payoutRatio = (dividendsPaid: number | null, netIncome: number | null): CalculationResult =>
  safeDivide(Math.abs(dividendsPaid ?? Number.NaN), netIncome);

export const netDebt = (totalDebt: number | null, cashAndEquivalents: number | null): CalculationResult => {
  if (!isFiniteNumber(totalDebt) || !isFiniteNumber(cashAndEquivalents)) return unavailable();
  return ok(totalDebt - cashAndEquivalents);
};

export const marginOfSafety = (intrinsicValue: number | null, currentPrice: number | null): CalculationResult => {
  if (!isFiniteNumber(intrinsicValue) || !isFiniteNumber(currentPrice)) return unavailable();
  if (intrinsicValue <= 0) return invalid("Intrinsic value must be positive");
  return ok((intrinsicValue - currentPrice) / intrinsicValue);
};

export const expectedFiveYearCagr = (currentPrice: number | null, expectedFiveYearPrice: number | null): CalculationResult =>
  fiveYearCagr(currentPrice, expectedFiveYearPrice, 5);

export const convertToMxn = (nativePrice: number | null, fxRate: number | null): CalculationResult => {
  if (!isFiniteNumber(nativePrice) || !isFiniteNumber(fxRate)) return unavailable();
  if (nativePrice < 0 || fxRate <= 0) return invalid("Price must be non-negative and FX rate must be positive");
  return ok(nativePrice * fxRate);
};

export interface ScoreInputs {
  businessQualityScore: number;
  financialStrengthScore: number;
  valuationScore: number;
  growthScore: number;
  managementScore: number;
  dividendScore: number;
  riskScore: number;
}

export const calculateBuffettScore = (scores: ScoreInputs): CalculationResult => {
  const max: Record<keyof ScoreInputs, number> = {
    businessQualityScore: 20,
    financialStrengthScore: 20,
    valuationScore: 20,
    growthScore: 15,
    managementScore: 10,
    dividendScore: 10,
    riskScore: 5
  };
  for (const [key, value] of Object.entries(scores) as [keyof ScoreInputs, number][]) {
    if (!isFiniteNumber(value) || value < 0 || value > max[key]) return invalid(`${key} must be between 0 and ${max[key]}`);
  }
  return ok(Object.values(scores).reduce((sum, value) => sum + value, 0));
};

export const calculateOpportunityScore = (
  marginOfSafetyValue: number | null,
  expectedCagr: number | null,
  buffettScore: number | null,
  dividendYieldValue: number | null
): CalculationResult => {
  if (!isFiniteNumber(marginOfSafetyValue) || !isFiniteNumber(expectedCagr) || !isFiniteNumber(buffettScore)) return unavailable();
  const dividend = isFiniteNumber(dividendYieldValue) ? dividendYieldValue : 0;
  const score =
    Math.min(35, Math.max(0, marginOfSafetyValue / 0.4) * 35) +
    Math.min(35, Math.max(0, expectedCagr / 0.2) * 35) +
    Math.min(20, (buffettScore / 100) * 20) +
    Math.min(10, Math.max(0, dividend / 0.05) * 10);
  return ok(Math.round(score * 10) / 10);
};

export interface GoldZoneInputs {
  buffettScore: number | null;
  opportunityScore: number | null;
  marginOfSafety: number | null;
  expectedCagr: number | null;
  normalizedFreeCashFlow: number | null;
  unresolvedCriticalWarning: boolean;
  acceptableDebtStatus: boolean;
}

export const classifyGoldZone = (inputs: GoldZoneInputs, settings: GoldZoneSettings): Zone => {
  const { buffettScore, opportunityScore, marginOfSafety: margin, expectedCagr } = inputs;
  const hasMissingCore =
    !isFiniteNumber(buffettScore) ||
    !isFiniteNumber(opportunityScore) ||
    !isFiniteNumber(margin) ||
    !isFiniteNumber(expectedCagr);

  if (hasMissingCore || inputs.unresolvedCriticalWarning || !inputs.acceptableDebtStatus) return "Avoid";
  if (settings.requirePositiveFcf && (!isFiniteNumber(inputs.normalizedFreeCashFlow) || inputs.normalizedFreeCashFlow <= 0)) return "Avoid";

  if (
    buffettScore >= settings.minimumBuffettScore &&
    opportunityScore >= settings.minimumOpportunityScore &&
    margin >= settings.minimumMarginOfSafety &&
    expectedCagr >= settings.minimumExpectedCagr &&
    (!settings.requireNoCriticalWarning || !inputs.unresolvedCriticalWarning)
  ) {
    return "Gold";
  }

  if (buffettScore >= 80 && opportunityScore >= 75 && margin >= 0.1 && expectedCagr >= 0.11) return "Silver";
  if (buffettScore >= 75 && margin >= 0.05) return "Watch";
  if (margin >= -0.05 && margin <= 0.1) return "Fair Value";
  return "Avoid";
};

export const dataAgeStatus = (dateIso: string | null, maxAgeDays: number, now = new Date()): "sample" | "stale" | "ok" | "unavailable" => {
  if (!dateIso) return "unavailable";
  const date = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "unavailable";
  const ageDays = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
  return ageDays > maxAgeDays ? "stale" : "ok";
};

